import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CATEGORIES, type Category } from '@sfp/shared/roles';
import { useAuth } from '../../lib/auth.js';
import { useCollection, orderBy, limit, where } from '../../lib/hooks.js';
import { categoryLabel, formatTimestamp } from '../../lib/format.js';
import { UploadButton } from './UploadButton.js';
import { SearchBar } from './SearchBar.js';

interface DocRow {
  id: string;
  category?: Category;
  docType?: string;
  originalName?: string;
  summary?: string;
  parties?: string[];
  uploadedAt?: { seconds: number };
  status?: string;
  filingEntity?: string;
}

export function DocumentsList() {
  const { allowedCategories, role } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const entityFilter = searchParams.get('entity');
  const [filter, setFilter] = useState<Category | 'all'>('all');
  const [searchResults, setSearchResults] = useState<DocRow[] | null>(null);

  const visibleCategories: readonly Category[] =
    role === 'admin' ? CATEGORIES : allowedCategories.length ? allowedCategories : ['other'];

  // Firestore `in` queries are capped at 10 values. For non-admins with >10
  // categories we chunk; for admins we skip the category filter entirely so
  // every doc (including the default 'other' that fresh uploads land in) shows.
  const skipFilter = role === 'admin' && filter === 'all';
  const categoriesForQuery: Category[] =
    filter === 'all'
      ? (visibleCategories as Category[]).slice(0, 10)
      : [filter];
  const constraints = skipFilter
    ? [orderBy('uploadedAt', 'desc'), limit(100)]
    : [
        where('category', 'in', categoriesForQuery),
        orderBy('uploadedAt', 'desc'),
        limit(100),
      ];
  const { docs, loading, error } = useCollection<DocRow>('documents', constraints);

  // Entity tiles link here with ?entity=Name — narrow to that entity's docs.
  const base = searchResults ?? docs;
  const rows = entityFilter
    ? base.filter((d) => (d.filingEntity ?? '').trim() === entityFilter)
    : base;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
          <p className="text-sm text-stone-500">
            {searchResults
              ? `${searchResults.length} search results`
              : `${docs.length} documents`}
          </p>
        </div>
        <nav className="flex gap-2 text-xs">
          <Link to="/documents/library" className="rounded border border-stone-300 px-3 py-1.5 font-medium text-stone-700 hover:bg-stone-50">
            ⛁ Library (by entity)
          </Link>
          {role === 'admin' && (
            <Link to="/documents/processing" className="rounded border border-stone-300 px-3 py-1.5 font-medium text-stone-700 hover:bg-stone-50">
              ◔ Processing monitor
            </Link>
          )}
        </nav>
      </header>

      {entityFilter && (
        <div className="flex items-center justify-between rounded-lg border border-[color:var(--sfp-primary)]/30 bg-[color:var(--sfp-primary)]/5 px-4 py-2 text-xs">
          <span className="text-stone-700">
            Showing documents for entity <strong>{entityFilter}</strong> — {rows.length} doc(s)
          </span>
          <button
            type="button"
            onClick={() => {
              searchParams.delete('entity');
              setSearchParams(searchParams);
            }}
            className="rounded bg-white px-2 py-1 text-[11px] font-medium text-stone-600 ring-1 ring-stone-300 hover:bg-stone-50"
          >
            Clear ×
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-[#9C4A3A] bg-[#9C4A3A]/10 px-4 py-3 text-xs text-[#9C4A3A]">
          <div className="font-semibold">Couldn't load documents.</div>
          <div className="mt-1">{error.message}</div>
          <div className="mt-1 text-stone-600">
            If this says “permission denied”, your session is stale — sign out and back in to refresh
            your access, then reload.
          </div>
        </div>
      )}

      <SearchBar onResults={(r) => setSearchResults(r as DocRow[] | null)} />

      <UploadButton />

      <div className="flex flex-wrap gap-1.5">
        <FilterChip
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          label="All"
        />
        {visibleCategories.map((c) => (
          <FilterChip
            key={c}
            active={filter === c}
            onClick={() => setFilter(c)}
            label={categoryLabel(c)}
          />
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-stone-400">
            {searchResults
              ? 'No matching documents.'
              : 'No documents yet. Drop some files above.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Parties</th>
                <th className="px-4 py-2">Uploaded</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-2">
                    <Link to={`/documents/${d.id}`} className="font-medium text-stone-900">
                      {d.originalName ?? d.id}
                    </Link>
                    {d.summary && (
                      <div className="line-clamp-1 text-xs text-stone-500">{d.summary}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className="chip">{categoryLabel(d.category ?? 'other')}</span>
                    {d.docType && (
                      <span className="ml-1 text-xs text-stone-500">{d.docType}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-stone-600">
                    {(d.parties ?? []).slice(0, 3).join(', ')}
                  </td>
                  <td className="px-4 py-2 text-xs text-stone-500">
                    {formatTimestamp(d.uploadedAt)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusChip status={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-stone-900 text-stone-50'
          : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
      }`}
    >
      {label}
    </button>
  );
}

function StatusChip({ status }: { status: string | undefined }) {
  if (!status || status === 'ready') return <span className="chip-success">Ready</span>;
  if (status === 'triage') return <span className="chip-warn">Triage</span>;
  if (status === 'error') return <span className="chip-error">Error</span>;
  return <span className="chip">{status}</span>;
}
